import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap } from 'rxjs';
import { RequirementQualificationSettingOverrideClient } from '../../api/requirement-qualification-setting-override-client.service';
import { RequirementOverrideDetail } from '../../models/requirement-override-detail.model';
import {
    deleteRequirementManualReviewOverride,
    deleteRequirementManualReviewOverrideFailure,
    deleteRequirementManualReviewOverrideSuccessfully,
    deleteRequirementQualificationSettingsOverride,
    deleteRequirementQualificationSettingsOverrideFailure,
    deleteRequirementQualificationSettingsOverrideSuccessfully,
    loadRequirementOverrideDetails,
    loadRequirementOverrideDetailsFailure,
    loadRequirementOverrideDetailsSuccessfully,
    saveRequirementsManualReviewOverride,
    saveRequirementsManualReviewOverrideFailure,
    saveRequirementsManualReviewOverrideSuccessfully,
    saveRequirementsQualificationSettingsOverride,
    saveRequirementsQualificationSettingsOverrideFailure,
    saveRequirementsQualificationSettingsOverrideSuccessfully,
    setRequirementOverrideIsLoadingFlag
} from '../actions/requirement-override.actions';
import { ComplianceState } from '../state/compliance.state';
import { RequirementQualificationSettingOverrideDetailsDto } from '../../api/models/requirement-qualification-setting-override-details-dto.model';
import { DocumentTypeLevelType } from '../../api/enums/document-type-level-type.enum';
import { ToasterService } from 'src/app/shared/services/toaster.service';

@Injectable()
export class RequirementOverrideEffects {
    loadRequirementOverrideDetails$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(loadRequirementOverrideDetails),
            switchMap((action) => {
                this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: true }));
                return this._requirementQualificationSettingOverrideClient
                    .getRequirementOverrideDetails(
                        action.documentTypeId,
                        action.entityType as unknown as DocumentTypeLevelType,
                        action.entityId
                    )
                    .pipe(
                        map((result) => {
                            this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: false }));
                            return loadRequirementOverrideDetailsSuccessfully({
                                details: result as unknown as RequirementOverrideDetail
                            });
                        }),
                        catchError((result: unknown) => of(loadRequirementOverrideDetailsFailure({ error: result })))
                    );
            })
        );
    });

    saveRequirementsQualificationSettingsOverride$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(saveRequirementsQualificationSettingsOverride),
            switchMap((action) => {
                this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: true }));
                return this._requirementQualificationSettingOverrideClient
                    .saveRequirementQualificationSettingsOverride(action.documentTypeId, action.request)
                    .pipe(
                        map((result: RequirementQualificationSettingOverrideDetailsDto) => {
                            this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: false }));
                            this.toasterService.toast('Applied qualification settings override successfully.');
                            return saveRequirementsQualificationSettingsOverrideSuccessfully({ details: result });
                        }),
                        catchError((result: unknown) =>
                            of(saveRequirementsQualificationSettingsOverrideFailure({ error: result }))
                        )
                    );
            })
        );
    });

    deleteRequirementsQualificationSettingsOverride$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(deleteRequirementQualificationSettingsOverride),
            switchMap((action) => {
                this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: true }));
                return this._requirementQualificationSettingOverrideClient
                    .deleteRequirementQualificationOverride(
                        action.documentTypeId,
                        action.entityType as unknown as DocumentTypeLevelType,
                        action.entityId
                    )
                    .pipe(
                        map((result: RequirementQualificationSettingOverrideDetailsDto) => {
                            this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: false }));
                            this.toasterService.toast('Removed qualification override successfully.');
                            return deleteRequirementQualificationSettingsOverrideSuccessfully({ details: result });
                        }),
                        catchError((result: unknown) =>
                            of(deleteRequirementQualificationSettingsOverrideFailure({ error: result }))
                        )
                    );
            })
        );
    });

    saveRequirementManualReviewOverride$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(saveRequirementsManualReviewOverride),
            switchMap((action) => {
                this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: true }));
                return this._requirementQualificationSettingOverrideClient
                    .saveRequirementManualReviewSettingsOverride(action.documentTypeId, action.request)
                    .pipe(
                        map((result: RequirementQualificationSettingOverrideDetailsDto) => {
                            this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: false }));
                            this.toasterService.toast('Applied manual review settings override successfully.');
                            return saveRequirementsManualReviewOverrideSuccessfully({ details: result });
                        }),
                        catchError((result: unknown) =>
                            of(saveRequirementsManualReviewOverrideFailure({ error: result }))
                        )
                    );
            })
        );
    });

    deleteRequirementManualReviewOverride$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(deleteRequirementManualReviewOverride),
            switchMap((action) => {
                this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: true }));
                return this._requirementQualificationSettingOverrideClient
                    .deleteRequirementManualReviewOverride(
                        action.documentTypeId,
                        action.entityType as unknown as DocumentTypeLevelType,
                        action.entityId
                    )
                    .pipe(
                        map((result: RequirementQualificationSettingOverrideDetailsDto) => {
                            this._store.dispatch(setRequirementOverrideIsLoadingFlag({ isLoading: false }));
                            this.toasterService.toast('Removed manual review settings override successfully.');
                            return deleteRequirementManualReviewOverrideSuccessfully({ details: result });
                        }),
                        catchError((result: unknown) =>
                            of(deleteRequirementManualReviewOverrideFailure({ error: result }))
                        )
                    );
            })
        );
    });

    constructor(
        private readonly _actions$: Actions,
        private readonly _requirementQualificationSettingOverrideClient: RequirementQualificationSettingOverrideClient,
        private readonly _store: Store<ComplianceState>,
        private readonly toasterService: ToasterService
    ) {}
}
